import { ContentType } from '@kanbu/schema/enums';
import { File, FileSymlink, Globe } from 'lucide-react';
import { memo } from 'react';

import {
  formatPageLabel,
  getHref,
  getSourceDisplayLabel,
  type RagSource,
} from './source-utils';
import { cn } from '../../lib/cn';

const chipClassName = cn(
  'inline-block rounded-full bg-chat-surface px-2 py-1',
  'text-[0.65rem] font-medium leading-none text-chat-surface-foreground no-underline',
  'transition-colors hover:bg-chat-primary hover:text-chat-primary-foreground',
);

const superscriptClassName = cn(
  'inline-flex min-w-[1rem] items-center justify-center align-super',
  'text-[0.6rem] font-semibold leading-none tabular-nums',
  'rounded-full bg-chat-surface px-1 py-0.5 mx-px',
  'text-chat-surface-foreground no-underline',
  'transition-colors hover:bg-chat-primary hover:text-chat-primary-foreground',
);

export interface SourceChipProps {
  /** Absent while the answer streams — renders a non-link numeric placeholder. */
  source?: RagSource;
  /** 1-based citation index shown for the superscript variant. */
  number?: number;
  variant?: 'inline' | 'superscript';
  className?: string;
}

function SourceIcon({ type }: { type: ContentType }) {
  if (type === ContentType.Document) {
    return <File className='size-3 shrink-0' />;
  }

  if (type === ContentType.Website) {
    return <Globe className='size-3 shrink-0' />;
  }

  if (type === ContentType.ExternalDocument) {
    return <FileSymlink className='size-3 shrink-0' />;
  }

  return null;
}

export const SourceChip = memo(
  ({ source, number, variant = 'inline', className }: SourceChipProps) => {
    /**
     * Superscript variant: a small numeric index at the claim. It links to
     * the source (PDF page anchor) once resolved and shows the human label
     * on hover; the full readable label lives in the sources block below.
     */
    if (variant === 'superscript') {
      const label = source ? getSourceDisplayLabel(source) : undefined;
      const href = source?.url
        ? getHref(source.url, source.page, source.mimeType)
        : undefined;
      const classNames = cn(superscriptClassName, className);

      if (href) {
        return (
          <a
            className={classNames}
            href={href}
            target='_blank'
            rel='noreferrer'
            title={label}
          >
            {number}
          </a>
        );
      }

      return (
        <span className={cn(classNames, 'cursor-default')} title={label}>
          {number}
        </span>
      );
    }

    if (!source) {
      return null;
    }

    const label = getSourceDisplayLabel(source);
    const pageLabel = formatPageLabel(source.page, source.type);
    const href = source.url
      ? getHref(source.url, source.page, source.mimeType)
      : undefined;
    const classNames = cn(chipClassName, 'cursor-pointer', className);

    if (href) {
      return (
        <a className={classNames} href={href} target='_blank' rel='noreferrer'>
          {label}
          {pageLabel && <span className='opacity-70'> · p. {pageLabel}</span>}
        </a>
      );
    }

    return (
      <span
        className={cn(
          chipClassName,
          'cursor-default inline-flex items-center gap-1',
          className,
        )}
      >
        <SourceIcon type={source.type} />
        <span className='truncate max-w-[12rem]'>{label}</span>
        {pageLabel && <span className='opacity-70'>· p. {pageLabel}</span>}
      </span>
    );
  },
);
